import { Analysis, CitationNode, CollectionName, PublicationSheet, ModelType } from '@livv/models';
import {
    getArticleLink,
    getDecisionLink,
    getSheetLink,
    getStatisticLink,
} from '@livv/utils/helpers';
import { collection, doc, getDoc } from 'firebase/firestore';
import { useEffect, useState } from 'react';
import { db } from '@config/firebase/client';

const getAnalysisUrl = async (id: string): Promise<string> => {
    const snapshot = await getDoc(doc(collection(db, CollectionName.ANALYSES), id));
    if (!snapshot.exists()) {
        throw new Error(`No analysis found with id ${id}`);
    }
    const { decisionId } = snapshot.data() as Analysis;

    return getDecisionLink(decisionId, id);
};

const getPublicationSheetUrl = async (id: string): Promise<string> => {
    const snapshot = await getDoc(doc(collection(db, CollectionName.PUBLICATION_SHEETS), id));
    if (!snapshot.exists()) {
        throw new Error(`No publication sheet found with id ${id}`);
    }
    const {
        keyword: { treeId, path },
    } = snapshot.data() as PublicationSheet;

    return getSheetLink({ bookId: treeId, bookPath: path.join('/'), sheetId: id });
};

const useDocumentLink = ({ id, type }: CitationNode): [string | null, boolean] => {
    const [url, setUrl] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const getUrl = async () => {
            switch (type) {
                case ModelType.ANALYSIS:
                    setUrl(await getAnalysisUrl(id));
                    break;
                case ModelType.ARTICLE:
                    setUrl(getArticleLink(id));
                    break;
                case ModelType.DECISION:
                    setUrl(getDecisionLink(id));
                    break;
                case ModelType.PUBLICATION_SHEET:
                    setUrl(await getPublicationSheetUrl(id));
                    break;
                case ModelType.STATISTIC:
                    setUrl(getStatisticLink(id));
                    break;
                default:
            }
            setIsLoading(false);
        };

        getUrl();
    }, [id, type]);

    return [url, isLoading];
};

export default useDocumentLink;
